/**
 * dcent-app-page.js — 모바일 앱 지갑 페이지의 두 인터랙션.
 *
 * 소스: 20260727 homepage 드롭 src/components/AppPortfolio.tsx · AppSwapSteps.tsx.
 * React state로 돌던 동작을 프레임워크 없이 DOM 클래스/스타일 토글로 재현한다.
 *
 *  1) 포트폴리오 캐러셀 — PC는 버튼으로 트랙을 transform 슬라이드, 모바일은 네이티브 가로
 *     스크롤 + 화살표·세그먼트 인디케이터.
 *  2) 스왑 3스텝 탭 — 탭 영역이 60% 이상 보이는 동안 일정 주기로 다음 스텝으로 자동 전환하고
 *     마지막에서 정지(루프 없음). 벗어나면 1번으로 리셋 → 재진입 시 처음부터.
 *
 * 전역 상태 0 · 다중 호출 안전(초기화 완료 루트에 플래그). 테마 편집기 재로드 대응.
 * 히어로 지표 카운트업은 공유 dcent-counter.js가 담당한다(여기 없음).
 *
 * 트랙 끝 판정(reachable end) — 원본 React가 놓친 지점:
 *   원본은 목표 스크롤을 i × step 으로만 계산해서, 트랙이 더 못 미는 폭(태블릿 768~1023 등)에서는
 *   next 버튼이 살아 있는 채로 먹통이 된다. 여기서는 목표 위치를 실제 최대 스크롤로 clamp 하고
 *   "끝까지 밀렸는가"로 next를 막는다.
 *
 *   ⚠ 인덱스 상한(floor(maxShift / step))으로 자르면 안 된다 — step의 배수가 아닌 나머지
 *   픽셀만큼 마지막 카드가 영영 잘린 채로 남는다. PC 뷰포트는 overflow:hidden 이라 손 스크롤
 *   대안도 없다(2026-07-28 재검토 H1에서 그 회귀를 되돌렸다).
 *   끝에 닿으면 마지막 카드가 온전히 보이므로 인디케이터도 마지막으로 맞춘다.
 */

const GAP = 16; // 카드 사이 간격(px) — CSS gap과 같은 값
const TAB_DURATION = 2000; // 스왑 탭 자동 전환 주기(ms) — 원본 DURATION. CSS는 이 값을 var로 받는다.

/** 모션 최소화 선호 시 smooth 스크롤·자동 전환을 즉시 이동으로 낮춘다. */
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/* ── 1) 포트폴리오 캐러셀 ─────────────────────────────────────────── */

function initPortfolio(root) {
  root.querySelectorAll("[data-dcent-app-portfolio]").forEach((sec) => {
    if (sec.__dcentAppPortfolio) return;
    sec.__dcentAppPortfolio = true;
    initPortfolioPc(sec);
    initPortfolioMobile(sec);
  });
}

function initPortfolioPc(sec) {
  const viewport = sec.querySelector("[data-dcent-app-pc-viewport]");
  const track = sec.querySelector("[data-dcent-app-pc-track]");
  const prev = sec.querySelector("[data-dcent-app-pc-prev]");
  const next = sec.querySelector("[data-dcent-app-pc-next]");
  if (!viewport || !track || !prev || !next) return;

  const cards = track.querySelectorAll(".dcent-app-pcard");
  const last = Math.max(0, cards.length - 1);
  let idx = 0;

  const metrics = () => {
    const card = cards[0];
    // 폴백 400 = CSS .dcent-app-pcard 의 PC 폭. 실측(offsetWidth)이 우선이라 동작에는 안 쓰이지만,
    // 값이 어긋나 있으면 다음 사람이 카드 폭을 여기서 잘못 읽는다 (2026-08-14 440→400 정렬).
    const step = (card ? card.offsetWidth : 400) + GAP;
    const maxShift = Math.max(0, track.scrollWidth - viewport.clientWidth);
    return { step, maxShift };
  };

  const go = (i) => {
    const { step, maxShift } = metrics();
    idx = Math.max(0, Math.min(last, i));
    // 목표 이동량만 실제 최대치로 clamp 한다 — 인덱스는 자르지 않는다.
    const shift = Math.min(idx * step, maxShift);
    track.style.transform = "translateX(" + -shift + "px)";
    prev.disabled = idx === 0;
    // 트랙이 끝까지 밀린 뒤에만 next를 막는다(그때 마지막 카드가 온전히 보인다).
    next.disabled = shift >= maxShift - 1 || idx === last;
  };

  prev.addEventListener("click", () => go(idx - 1));
  next.addEventListener("click", () => go(idx + 1));
  // 폭이 바뀌면 step·maxShift가 달라지므로 현재 인덱스로 다시 계산
  window.addEventListener("resize", () => go(idx));
  go(0);
}

function initPortfolioMobile(sec) {
  const scroller = sec.querySelector("[data-dcent-app-m-scroller]");
  const dots = sec.querySelectorAll("[data-dcent-app-m-dot]");
  const prev = sec.querySelector("[data-dcent-app-m-prev]");
  const next = sec.querySelector("[data-dcent-app-m-next]");
  if (!scroller || dots.length === 0 || !prev || !next) return;

  const cards = scroller.querySelectorAll(".dcent-app-pcard");
  const last = Math.max(0, cards.length - 1);
  let active = 0;

  const metrics = () => {
    const card = cards[0];
    const step = card ? card.offsetWidth + GAP : 336;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    return { step, maxScroll };
  };

  /**
   * 활성 인덱스·버튼 상태를 스크롤 위치로 확정한다(인덱스 상한을 쓰지 않는다).
   * assumedLeft — 클릭 직후 smooth 스크롤이 끝나기 전에 목표 위치로 먼저 반영할 때 쓴다.
   */
  const paint = (assumedLeft) => {
    const { step, maxScroll } = metrics();
    const left =
      typeof assumedLeft === "number" ? assumedLeft : scroller.scrollLeft;
    // 끝까지 밀린 상태면 마지막 카드가 온전히 보이므로 인디케이터도 마지막으로 맞춘다.
    active =
      maxScroll > 0 && left >= maxScroll - 1
        ? last
        : Math.max(0, Math.min(last, Math.round(left / step)));
    dots.forEach((d, i) => d.classList.toggle("is-active", i === active));
    prev.disabled = left <= 0;
    next.disabled = left >= maxScroll - 1;
  };

  const scrollToIndex = (i) => {
    const { step, maxScroll } = metrics();
    const target = Math.max(0, Math.min(last, i));
    // 목표 위치만 clamp — 마지막 카드로 갈 때 남은 나머지 픽셀까지 끝까지 민다.
    const left = Math.min(target * step, maxScroll);
    scroller.scrollTo({
      left,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
    paint(left);
  };

  scroller.addEventListener("scroll", () => paint());
  prev.addEventListener("click", () => scrollToIndex(active - 1));
  next.addEventListener("click", () => scrollToIndex(active + 1));
  dots.forEach((d, i) => d.addEventListener("click", () => scrollToIndex(i)));
  window.addEventListener("resize", () => paint());
  paint();
}

/* ── 2) 스왑 3스텝 자동 탭 ────────────────────────────────────────── */

function initSwapTabs(root) {
  root.querySelectorAll("[data-dcent-app-tabs]").forEach((tabs) => {
    if (tabs.__dcentAppTabs) return;
    tabs.__dcentAppTabs = true;

    const items = tabs.querySelectorAll("[data-dcent-app-tab]");
    if (items.length === 0) return;
    const last = items.length - 1;
    let active = 0;
    let inView = false;
    let timer = 0;

    // 진행 바 길이는 JS가 단일 소스 — CSS는 var(--dap-tab-duration)로 받는다(두 곳 드리프트 방지).
    tabs.style.setProperty("--dap-tab-duration", TAB_DURATION + "ms");

    const paint = () => {
      items.forEach((el, i) => {
        const on = i === active;
        el.classList.toggle("is-open", on);
        // 진행 바는 보이는 동안에만 재생 — 클래스를 떼었다 붙여 애니메이션을 처음부터 다시 돌린다
        el.classList.remove("is-playing");
        if (on && inView) {
          void el.offsetWidth;
          el.classList.add("is-playing");
        }
        el.setAttribute("aria-expanded", on ? "true" : "false");
      });
    };

    const schedule = () => {
      clearTimeout(timer);
      // 모션 최소화 선호 시 자동 전환을 걸지 않는다(사용자가 직접 탭을 고른다).
      if (!inView || active >= last || prefersReducedMotion()) return;
      timer = setTimeout(() => {
        active += 1;
        paint();
        schedule();
      }, TAB_DURATION);
    };

    const setActive = (i) => {
      active = Math.max(0, Math.min(last, i));
      paint();
      schedule();
    };

    items.forEach((el, i) => el.addEventListener("click", () => setActive(i)));

    if (typeof IntersectionObserver === "function") {
      const io = new IntersectionObserver(
        ([entry]) => {
          const on = entry.isIntersecting && entry.intersectionRatio >= 0.6;
          if (on === inView) return;
          inView = on;
          if (!on) active = 0;
          paint();
          schedule();
        },
        { threshold: [0, 0.6] },
      );
      io.observe(tabs);
    } else {
      inView = true;
    }
    paint();
  });
}

export function initAppPage(root = document) {
  initPortfolio(root);
  initSwapTabs(root);
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initAppPage());
  document.addEventListener("shopify:section:load", (e) =>
    initAppPage(e.target),
  );
}
